import { matchPath } from "react-router-dom";
import type {
  StudentToofiSurface,
  StudentToofiSurfaceContext,
  StudentToofiSurfaceRegistration,
} from "@/types/student-toofi";

const SURFACE_LABELS: Record<StudentToofiSurface, { title: string; subtitle: string }> = {
  dashboard: {
    title: "Dashboard",
    subtitle: "Ask about plans, progress, and what to study next.",
  },
  library: {
    title: "Library",
    subtitle: "Ask which resources to use and what to study first.",
  },
  resource_detail: {
    title: "Resource",
    subtitle: "Ask how this resource fits your study plan.",
  },
  profile: {
    title: "Profile",
    subtitle: "Ask about memory, preferences, and how Toofi adapts.",
  },
  review: {
    title: "Review",
    subtitle: "Ask about weak areas, review, and what to fix next.",
  },
  study_time: {
    title: "Study time",
    subtitle: "Ask about habits, pacing, and your study rhythm.",
  },
  inbox: {
    title: "Inbox",
    subtitle: "Ask what matters now and which messages need action.",
  },
  my_library: {
    title: "My library",
    subtitle: "Ask which of your saved resources to use next.",
  },
  my_passes: {
    title: "My passes",
    subtitle: "Ask how to use your passes and what to study first.",
  },
  pass_detail: {
    title: "Pass details",
    subtitle: "Ask how this pass fits your plan and what to open next.",
  },
  my_orders: {
    title: "Orders",
    subtitle: "Ask about access, purchases, and what is active now.",
  },
  offline_library: {
    title: "Offline library",
    subtitle: "Ask what you can study offline right now.",
  },
  ai_plan: {
    title: "Toofi plan",
    subtitle: "Ask about your AI plan, limits, and included features.",
  },
  unknown: {
    title: "Toofi",
    subtitle: "Ask about your study, resources, plans, or what to do next.",
  },
};

export const getStudentToofiSurface = (pathname: string): StudentToofiSurface => {
  if (pathname === "/dashboard" || pathname === "/student/dashboard") return "dashboard";
  if (pathname === "/library" || pathname === "/student/library") return "library";
  if (pathname === "/library/offline" || pathname === "/student/library/offline") return "offline_library";
  if (pathname === "/profile" || pathname === "/student/profile") return "profile";
  if (pathname === "/review" || pathname === "/student/review") return "review";
  if (pathname === "/study-time" || pathname === "/student/study-time") return "study_time";
  if (pathname === "/inbox" || pathname === "/student/inbox") return "inbox";
  if (pathname === "/my-library" || pathname === "/student/my-library") return "my_library";
  if (pathname === "/student/passes") return "my_passes";
  if (matchPath("/student/passes/:passId", pathname)) return "pass_detail";
  if (pathname === "/my-orders" || pathname === "/student/orders") return "my_orders";
  if (pathname === "/ai-plan" || pathname === "/student/ai-plan") return "ai_plan";
  if (matchPath("/resource/:id", pathname) || matchPath("/student/resource/:id", pathname)) return "resource_detail";
  return "unknown";
};

export const buildStudentToofiSurfaceContext = ({
  pathname,
  registration,
}: {
  pathname: string;
  registration?: StudentToofiSurfaceRegistration | null;
}): StudentToofiSurfaceContext => {
  const surface = getStudentToofiSurface(pathname);
  const defaults = SURFACE_LABELS[surface];
  const resourceMatch = matchPath("/resource/:id", pathname) ?? matchPath("/student/resource/:id", pathname);
  return {
    surface,
    routePath: pathname,
    title: registration?.title ?? defaults.title,
    subtitle: registration?.subtitle ?? defaults.subtitle,
    linkedResourceId: registration?.linkedResourceId ?? resourceMatch?.params.id ?? null,
    linkedPlanId: registration?.linkedPlanId ?? null,
    metadata: registration?.metadata ?? undefined,
  };
};
